import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchIndividualClientFile } from "../../../store/slices/individualClientFileSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

// Checkbox glyph
const cb = (v) => (v ? "☑" : "☐"); // ☑ / ☐
const fmt = (v) => (v ? new Date(v).toLocaleDateString("en-GB") : "—");
const val = (v) => (v === null || v === undefined || v === "" ? "—" : v);

// Public storage base for saved signature images
const STORAGE_URL = "https://trh-backend.clouxiplexi.com/storage/";
const sigSrc = (v) => (!v ? null : v.startsWith("data:") ? v : `${STORAGE_URL}${v}`);

export default function IndividualClientFilePrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { currentIndividualClientFile, isLoading, error } = useSelector(
    (s) => s.individualClientFile
  );

  useEffect(() => {
    if (id) dispatch(fetchIndividualClientFile(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentIndividualClientFile) {
      const t = setTimeout(() => window.print(), 800);
      window.onafterprint = () => navigate(-1);
      return () => {
        clearTimeout(t);
        window.onafterprint = null;
      };
    }
  }, [currentIndividualClientFile, navigate]);

  if (isLoading) return <div className="p-4 text-center">Loading Individual Client File print...</div>;
  if (error) return <div className="p-4 text-danger text-center">{error}</div>;
  if (!currentIndividualClientFile) return null;

  const d = currentIndividualClientFile;
  const goals = d.goals || [];
  // API serializes relations as snake_case keys
  const progressNotes = d.progress_notes || [];
  const familyMeetings = d.family_meetings || [];
  const transitionActivities = d.transition_activities || [];

  const CheckItem = ({ on, children }) => (
    <div className="chk">
      <span className="box">{cb(on)}</span> {children}
    </div>
  );

  const SectionTitle = ({ children }) => <div className="section-title">{children}</div>;
  const SubTitle = ({ children }) => <div className="sub-title">{children}</div>;

  return (
    <>
      <style jsx global>{`
        body { margin: 0 !important; padding: 0 !important; }
        ${printFooterStyles}
        @media print {
          @page { size: A4; margin: 14mm 12mm; }
          body { background: #fff !important; color: #000 !important; }
          .no-print { display: none !important; }
          .section, .card-block, .chk-grid, table { page-break-inside: avoid; }
          /* Force Chrome/Safari to keep table borders & header shading in print */
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          table { border-collapse: collapse !important; }
          table, th, td { border: 1px solid #000 !important; }
          th { background: #f0f0f0 !important; }
          .section-title { border: 1px solid #000 !important; background: #e9e9e9 !important; }
          .notes-box, .role-def, .signature-line { border: 1px solid #000 !important; }
          .role-def { background: #f5f5f5 !important; }
          .lbl-bold, .lbl { background: #f7f7f7 !important; }
        }

        * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .icf-print, .icf-print * {
          font-family: "Times New Roman", Times, serif;
          color: #000;
        }
        .icf-print {
          width: 100%;
          max-width: 820px;
          margin: 0 auto;
          padding: 18px;
          font-size: 10.5pt;
          line-height: 1.35;
          text-align: left !important;
        }
        .header-logo { text-align: left !important; margin-bottom: 6px; }
        .header-logo img { height: 62px; }
        .doc-title {
          text-align: left !important;
          font-size: 17pt;
          font-weight: bold;
          text-transform: uppercase;
          margin: 0;
          letter-spacing: 1px;
        }
        .doc-sub { text-align: left !important; font-size: 11pt; font-weight: bold; margin: 2px 0; }
        .doc-tagline { text-align: left !important; font-size: 9pt; font-style: italic; margin: 0 0 6px; }
        .role-def {
          border: 1px solid #000;
          padding: 5px 8px;
          font-size: 8.7pt;
          margin-bottom: 14px;
          background: #f5f5f5;
        }
        .section { margin-bottom: 16px; }
        .section-title {
          font-size: 11.5pt;
          font-weight: bold;
          background: #e9e9e9;
          border: 1px solid #000;
          padding: 4px 8px;
          text-transform: uppercase;
          margin-bottom: 8px;
          text-align: left !important;
        }
        .sub-title {
          font-size: 10pt;
          font-weight: bold;
          margin: 8px 0 4px;
          text-decoration: underline;
          text-align: left !important;
        }
        table { width: 100%; border-collapse: collapse; margin-bottom: 8px; }
        td, th { border: 1px solid #000; padding: 5px 7px; font-size: 9.7pt; vertical-align: top; }
        th { background: #f0f0f0; text-align: left; }
        .lbl { font-weight: bold; background: #f7f7f7; width: 24%; }
        .chk-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2px 16px;
          margin-bottom: 6px;
        }
        .chk-grid.one { grid-template-columns: 1fr; }
        .chk { font-size: 9.7pt; padding: 1px 0; }
        .chk .box { font-size: 11pt; }
        .notes-box {
          border: 1px solid #000;
          min-height: 46px;
          padding: 5px 7px;
          white-space: pre-wrap;
          font-size: 9.7pt;
          margin-bottom: 6px;
          text-align: left !important;
        }
        .goal-block, .note-block {
          border: 1px solid #000;
          padding: 6px 8px;
          margin-bottom: 8px;
          text-align: left !important;
        }
        .goal-head { font-weight: bold; font-size: 10pt; border-bottom: 1px solid #000; padding-bottom: 3px; margin-bottom: 5px; }
        .goal-meta { display: flex; flex-wrap: wrap; gap: 4px 18px; font-size: 9.3pt; margin-top: 4px; }
        .signoff td { height: 34px; }
        .footer-badges { text-align: left; font-size: 8.7pt; font-weight: bold; margin-top: 14px; border-top: 1px solid #000; padding-top: 6px; }
        .confidential { text-align: left; font-size: 8pt; font-style: italic; margin-top: 4px; }
      `}</style>

      <PrintWrapper>
        <div className="icf-print">
          {/* HEADER */}
          <div className="header-logo">
            <img src={logo} alt="The Recovery House Logo" />
          </div>
          <h1 className="doc-title">Individual Client File</h1>
          <div className="doc-sub">Comprehensive Psychiatric Rehabilitation Framework (CARF-Aligned)</div>
          <div className="doc-tagline">
            Recovery-Oriented &middot; Collaborative &middot; Stage-Based &middot; Evidence-Informed &middot; LEAP-Integrated
          </div>
          <div className="role-def">
            <strong>Role Definitions:</strong>&nbsp; PRP = Psychiatric Rehabilitation Practitioner (direct rehabilitation
            services) &nbsp;|&nbsp; Case Manager = Care coordinator (service linkage, monitoring, planning)
          </div>

          {/* 1. CLIENT IDENTIFICATION & INTAKE */}
          <div className="section">
            <SectionTitle>1. Client Identification &amp; Intake</SectionTitle>
            <table>
              <tbody>
                <tr><td className="lbl">Client Name</td><td>{val(d.client_name)}</td><td className="lbl">Client ID / File No</td><td>{val(d.client_file_no)}</td></tr>
                <tr><td className="lbl">Date of Birth</td><td>{fmt(d.date_of_birth)}</td><td className="lbl">Age</td><td>{val(d.age)}</td></tr>
                <tr><td className="lbl">Gender</td><td>{val(d.gender)}</td><td className="lbl">Diagnosis (if applicable)</td><td>{val(d.diagnosis)}</td></tr>
                <tr><td className="lbl">Date of Enrollment</td><td>{fmt(d.date_of_enrollment)}</td><td className="lbl">Consultant Psychiatrist</td><td>{val(d.consultant_psychiatrist)}</td></tr>
                <tr><td className="lbl">Consultant Designation</td><td>{val(d.consultant_designation)}</td><td className="lbl">Case Manager Assigned</td><td>{val(d.case_manager_assigned)}</td></tr>
                <tr><td className="lbl">PRP Assigned</td><td>{val(d.prp_assigned)}</td><td className="lbl">Family Representative</td><td>{val(d.family_representative)}</td></tr>
              </tbody>
            </table>
            <CheckItem on={d.consent_collaborative_care}>Consent for Collaborative Care Obtained</CheckItem>
          </div>

          {/* 2. INITIAL MULTIDIMENSIONAL ASSESSMENT */}
          <div className="section">
            <SectionTitle>2. Initial Multidimensional Assessment (Baseline)</SectionTitle>
            <SubTitle>Clinical &amp; Functional</SubTitle>
            <div className="chk-grid">
              <CheckItem on={d.assess_mental_health_status}>Mental Health Status</CheckItem>
              <CheckItem on={d.assess_cognitive_functioning}>Cognitive Functioning</CheckItem>
              <CheckItem on={d.assess_insight_motivation}>Insight &amp; Motivation Level</CheckItem>
              <CheckItem on={d.assess_risk_assessment}>Risk Assessment</CheckItem>
            </div>
            <SubTitle>Life Skills Domains</SubTitle>
            <div className="chk-grid">
              <CheckItem on={d.assess_living_skills}>Living Skills (ADLs, hygiene)</CheckItem>
              <CheckItem on={d.assess_learning_skills}>Learning Skills</CheckItem>
              <CheckItem on={d.assess_working_skills}>Working Skills</CheckItem>
              <CheckItem on={d.assess_social_skills}>Social Skills</CheckItem>
            </div>
            <SubTitle>Recovery Capital</SubTitle>
            <div className="chk-grid">
              <CheckItem on={d.assess_family_support}>Family Support</CheckItem>
              <CheckItem on={d.assess_financial_stability}>Financial Stability</CheckItem>
              <CheckItem on={d.assess_housing}>Housing</CheckItem>
              <CheckItem on={d.assess_community_integration}>Community Integration</CheckItem>
            </div>
            <SubTitle>Assessment Summary / Clinician Notes</SubTitle>
            <div className="notes-box">{d.assessment_summary || ""}</div>
          </div>

          {/* 3. COLLABORATIVE GOAL SETTING */}
          <div className="section">
            <SectionTitle>3. Collaborative Goal Setting</SectionTitle>
            <div style={{ fontStyle: "italic", fontSize: "9pt", marginBottom: "6px" }}>
              Each goal must begin with: "I intend to…" — written in the client's own words
            </div>
            {goals.length === 0 && <div className="notes-box">No goals recorded.</div>}
            {goals.map((g, i) => (
              <div className="goal-block" key={g.id || i}>
                <div className="goal-head">Goal {i + 1}</div>
                <div><strong>I intend to…</strong> {val(g.intend_to)}</div>
                <div className="goal-meta">
                  <span><strong>Priority Rank:</strong> {val(g.priority_rank)}</span>
                  <span><strong>Target Date:</strong> {fmt(g.target_date)}</span>
                  <span><strong>Goal Achievement (%):</strong> {val(g.goal_achievement)}</span>
                  <span><strong>SMART:</strong> {cb(g.smart)} Yes</span>
                </div>
              </div>
            ))}
          </div>

          {/* 4. INDIVIDUALIZED REHABILITATION PLAN */}
          <div className="section">
            <SectionTitle>4. Individualized Rehabilitation Plan (IRP)</SectionTitle>
            <SubTitle>Program Selection</SubTitle>
            <div className="chk-grid">
              <CheckItem on={d.irp_residential}>Residential</CheckItem>
              <CheckItem on={d.irp_day_program}>Day Program</CheckItem>
              <CheckItem on={d.irp_home_based}>Home-Based / Outreach</CheckItem>
            </div>
            <SubTitle>Program Customization</SubTitle>
            <div className="chk-grid">
              <CheckItem on={d.irp_living_skills}>Living Skills</CheckItem>
              <CheckItem on={d.irp_learning_skills}>Learning Skills</CheckItem>
              <CheckItem on={d.irp_working_skills}>Working Skills</CheckItem>
              <CheckItem on={d.irp_social_skills}>Social Skills</CheckItem>
              <CheckItem on={d.irp_session_plan_codeveloped}>Session Plan Co-developed</CheckItem>
              <CheckItem on={d.irp_frequency_duration_defined}>Frequency &amp; Duration Defined</CheckItem>
            </div>
            <table>
              <tbody>
                <tr><td className="lbl">Session Frequency</td><td>{val(d.session_frequency)}</td><td className="lbl">Session Duration</td><td>{val(d.session_duration)}</td></tr>
                <tr><td className="lbl">Program Start Date</td><td colSpan={3}>{fmt(d.program_start_date)}</td></tr>
              </tbody>
            </table>
          </div>

          {/* 5. EVIDENCE-BASED INTERVENTIONS */}
          <div className="section">
            <SectionTitle>5. Evidence-Based Interventions</SectionTitle>
            <SubTitle>Core Interventions</SubTitle>
            <div className="chk-grid">
              <CheckItem on={d.int_psychoeducation}>Psychoeducation</CheckItem>
              <CheckItem on={d.int_cbt}>CBT-Based Interventions</CheckItem>
              <CheckItem on={d.int_social_skills_training}>Social Skills Training</CheckItem>
              <CheckItem on={d.int_occupational_therapy}>Occupational Therapy</CheckItem>
              <CheckItem on={d.int_behavioral_activation}>Behavioral Activation</CheckItem>
              <CheckItem on={d.int_family_therapy}>Family Therapy</CheckItem>
              <CheckItem on={d.int_vocational_training}>Vocational Training</CheckItem>
            </div>
            <SubTitle>Specialist Evidence-Based Programs</SubTitle>
            <div className="chk-grid one">
              <CheckItem on={d.int_imr}>Illness Management &amp; Recovery (IMR) — structured curriculum for self-management, symptom coping, and personal recovery goals</CheckItem>
              <CheckItem on={d.int_wrap}>Wellness Recovery Action Planning (WRAP) — client-developed personal wellness and crisis plan</CheckItem>
              <CheckItem on={d.int_anti_stigma}>Anti-Stigma Intervention — psychoeducation and lived-experience dialogue to reduce self-stigma and social stigma</CheckItem>
              <CheckItem on={d.int_motivational_interviewing}>Motivational Interviewing (MI) — collaborative, goal-oriented approach to strengthen motivation for change</CheckItem>
            </div>
            <SubTitle>Intervention Notes / Rationale</SubTitle>
            <div className="notes-box">{d.intervention_notes || ""}</div>
          </div>

          {/* 6. PROGRESS MONITORING */}
          <div className="section">
            <SectionTitle>6. Progress Monitoring — Twice Weekly Notes</SectionTitle>
            {progressNotes.length === 0 && <div className="notes-box">No progress notes recorded.</div>}
            {progressNotes.map((p, i) => (
              <div className="note-block" key={p.id || i}>
                <div className="goal-head">Progress Note — Session {i + 1}</div>
                <table>
                  <tbody>
                    <tr><td className="lbl">Date</td><td>{fmt(p.note_date)}</td><td className="lbl">Goal Achievement (%)</td><td>{val(p.goal_achievement)}</td></tr>
                    <tr><td className="lbl">Symptoms</td><td colSpan={3}>{val(p.symptoms)}</td></tr>
                    <tr><td className="lbl">Functional Progress</td><td colSpan={3}>{val(p.functional_progress)}</td></tr>
                    <tr><td className="lbl">Participation</td><td colSpan={3}>{val(p.participation)}</td></tr>
                    <tr><td className="lbl">Barriers</td><td colSpan={3}>{val(p.barriers)}</td></tr>
                    <tr><td className="lbl">Adjustments / Plan</td><td colSpan={3}>{val(p.adjustments_plan)}</td></tr>
                  </tbody>
                </table>
              </div>
            ))}
          </div>

          {/* 7. STRUCTURED GOAL REVIEW CYCLE */}
          <div className="section">
            <SectionTitle>7. Structured Goal Review Cycle</SectionTitle>
            <table>
              <thead>
                <tr><th style={{ width: "18%" }}>Timeline</th><th style={{ width: "24%" }}>Action</th><th>Outcome / Notes</th></tr>
              </thead>
              <tbody>
                <tr><td>Weekly</td><td>Progress Check</td><td>{val(d.review_weekly_outcome)}</td></tr>
                <tr><td>Monthly</td><td>Evaluation</td><td>{val(d.review_monthly_outcome)}</td></tr>
                <tr><td>Quarterly</td><td>Plan Revision</td><td>{val(d.review_quarterly_outcome)}</td></tr>
                <tr><td>6-Monthly</td><td>Outcome Decision</td><td>{val(d.review_6monthly_outcome)}</td></tr>
              </tbody>
            </table>

            <SubTitle>Monthly Family Meetings</SubTitle>
            <div style={{ fontSize: "8.8pt", marginBottom: "5px" }}>
              Conducted monthly with client, family representative, PRP, and Case Manager to discuss progress, concerns, and rehabilitation planning.
            </div>
            <table>
              <thead>
                <tr><th style={{ width: "16%" }}>Meeting Date</th><th style={{ width: "24%" }}>Attendees</th><th>Key Concerns / Updates</th><th>Action Points</th></tr>
              </thead>
              <tbody>
                {familyMeetings.length === 0 && (
                  <tr><td colSpan={4} style={{ textAlign: "center" }}>—</td></tr>
                )}
                {familyMeetings.map((f, i) => (
                  <tr key={f.id || i}>
                    <td>{fmt(f.meeting_date)}</td>
                    <td>{val(f.attendees)}</td>
                    <td>{val(f.key_concerns)}</td>
                    <td>{val(f.action_points)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 8. STAGE-BASED REHABILITATION MODEL */}
          <div className="section">
            <SectionTitle>8. Stage-Based Rehabilitation Model</SectionTitle>
            <div style={{ fontSize: "8.8pt", marginBottom: "5px" }}>
              Minimum 60% Goal Achievement required for stage advancement.
            </div>
            <table>
              <thead>
                <tr><th>Stage</th><th>Role</th><th>Activities</th><th style={{ width: "16%" }}>Date Achieved</th></tr>
              </thead>
              <tbody>
                <tr><td>Stage 1</td><td>Client (Skill Building)</td><td>Structured program participation, core life skills</td><td>{fmt(d.stage1_date_achieved)}</td></tr>
                <tr><td>Stage 2</td><td>Observer</td><td>Observes sessions, builds insight, reduces stigma</td><td>{fmt(d.stage2_date_achieved)}</td></tr>
                <tr><td>Stage 3</td><td>Co-Facilitator</td><td>Assists sessions, applies skills</td><td>{fmt(d.stage3_date_achieved)}</td></tr>
                <tr><td>Stage 4</td><td>Facilitator</td><td>Leads sessions independently under PRP supervision</td><td>{fmt(d.stage4_date_achieved)}</td></tr>
              </tbody>
            </table>
            <table>
              <tbody>
                <tr><td className="lbl">Duration in Program</td><td>{val(d.duration_in_program)}</td></tr>
              </tbody>
            </table>
          </div>

          {/* 9. TRANSITION & REINTEGRATION PROCESS */}
          <div className="section">
            <SectionTitle>9. Transition &amp; Reintegration Process</SectionTitle>
            <div style={{ fontSize: "8.8pt", marginBottom: "6px" }}>
              Graduated transition plan to support a safe, structured return to community living. Documented collaboratively by client, PRP, Case Manager, and family.
            </div>
            <SubTitle>9A. Graduated Transition Activities</SubTitle>
            <table>
              <thead>
                <tr><th>Activity</th><th style={{ width: "13%" }}>Initiated Date</th><th style={{ width: "13%" }}>Frequency</th><th style={{ width: "16%" }}>Accompanied By</th><th style={{ width: "13%" }}>Status</th></tr>
              </thead>
              <tbody>
                {transitionActivities.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: "center" }}>—</td></tr>
                )}
                {transitionActivities.map((t, i) => (
                  <tr key={t.id || i}>
                    <td>{val(t.activity)}</td>
                    <td>{fmt(t.initiated_date)}</td>
                    <td>{val(t.frequency)}</td>
                    <td>{val(t.accompanied_by)}</td>
                    <td>{val(t.activity_status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <SubTitle>9B. Discharge Preparation</SubTitle>
            <div className="chk-grid">
              <CheckItem on={d.disch_criteria_reviewed}>Discharge criteria reviewed with client and family</CheckItem>
              <CheckItem on={d.disch_relapse_prevention}>Relapse prevention plan completed (WRAP or equivalent)</CheckItem>
              <CheckItem on={d.disch_community_support}>Community support services identified and linked</CheckItem>
              <CheckItem on={d.disch_medication_plan}>Medication management plan confirmed</CheckItem>
              <CheckItem on={d.disch_crisis_contacts}>Crisis contacts established</CheckItem>
              <CheckItem on={d.disch_followup_appointments}>Outpatient / follow-up appointments scheduled</CheckItem>
              <CheckItem on={d.disch_housing_confirmed}>Housing confirmed and safety-assessed</CheckItem>
              <CheckItem on={d.disch_family_orientation}>Family orientation and briefing conducted</CheckItem>
            </div>
            <table>
              <tbody>
                <tr><td className="lbl">Planned Discharge Date</td><td>{fmt(d.planned_discharge_date)}</td><td className="lbl">Reintegration Date</td><td>{fmt(d.reintegration_date)}</td></tr>
              </tbody>
            </table>
            <SubTitle>Reintegration Notes</SubTitle>
            <div className="notes-box">{d.reintegration_notes || ""}</div>
          </div>

          {/* 10. POST-REINTEGRATION FOLLOW-UP */}
          <div className="section">
            <SectionTitle>10. Post-Reintegration Follow-Up</SectionTitle>
            <div className="chk-grid">
              <CheckItem on={d.follow_case_manager}>Case Manager Assigned</CheckItem>
              <CheckItem on={d.follow_monitoring_plan}>Monitoring Plan</CheckItem>
              <CheckItem on={d.follow_relapse_prevention}>Relapse Prevention</CheckItem>
              <CheckItem on={d.follow_family_engagement}>Family Engagement</CheckItem>
              <CheckItem on={d.follow_employment_tracking}>Employment Tracking</CheckItem>
            </div>
            <SubTitle>Follow-Up Notes</SubTitle>
            <div className="notes-box">{d.followup_notes || ""}</div>
          </div>

          {/* 11. LEAP DOMAIN */}
          <div className="section">
            <SectionTitle>11. LEAP Domain (Lived Experience Advisory Panel)</SectionTitle>
            <SubTitle>Eligibility</SubTitle>
            <div className="chk-grid one">
              <CheckItem on={d.leap_goal_achievement}>≥ 60–80% Goal Achievement</CheckItem>
              <CheckItem on={d.leap_completed_facilitator}>Completed Facilitator Stage</CheckItem>
              <CheckItem on={d.leap_demonstrates_insight}>Demonstrates Insight, Stability, Stigma-Free Attitude, Emotional Regulation</CheckItem>
              <CheckItem on={d.leap_approved}>Approved by PRP, Case Manager, and Family</CheckItem>
            </div>
            <SubTitle>LEAP Role Assigned</SubTitle>
            <div className="chk-grid">
              <CheckItem on={d.leap_role_peer_support}>Peer Support Facilitator</CheckItem>
              <CheckItem on={d.leap_role_facilitator}>Facilitator / Co-Facilitator</CheckItem>
              <CheckItem on={d.leap_role_advisory}>Advisory Panel Member</CheckItem>
            </div>
            <SubTitle>Training Completed</SubTitle>
            <div className="chk-grid">
              <CheckItem on={d.leap_train_facilitation}>Facilitation Skills</CheckItem>
              <CheckItem on={d.leap_train_communication}>Communication &amp; Boundaries</CheckItem>
              <CheckItem on={d.leap_train_ethics}>Ethics &amp; Confidentiality</CheckItem>
              <CheckItem on={d.leap_train_mhfa}>Mental Health First Aid</CheckItem>
              <CheckItem on={d.leap_train_selfcare}>Self-Care &amp; Relapse Prevention</CheckItem>
            </div>
            <SubTitle>Participation Structure</SubTitle>
            <div className="chk-grid">
              <CheckItem on={d.leap_supervisor_assigned}>Supervisor Assigned</CheckItem>
              <CheckItem on={d.leap_role_defined}>Role Defined</CheckItem>
              <CheckItem on={d.leap_engagement_plan}>Engagement Plan</CheckItem>
              <CheckItem on={d.leap_supervision_scheduled}>Supervision Sessions Scheduled</CheckItem>
            </div>
            <SubTitle>Monitoring</SubTitle>
            <table>
              <thead>
                <tr><th style={{ width: "20%" }}>Timeline</th><th style={{ width: "26%" }}>Focus</th><th>Notes</th></tr>
              </thead>
              <tbody>
                <tr><td>Monthly</td><td>Role Performance</td><td>{val(d.leap_monitor_monthly)}</td></tr>
                <tr><td>Quarterly</td><td>Leadership Growth</td><td>{val(d.leap_monitor_quarterly)}</td></tr>
                <tr><td>6-Monthly</td><td>Continuation Decision</td><td>{val(d.leap_monitor_6monthly)}</td></tr>
              </tbody>
            </table>
            <SubTitle>LEAP Outcomes</SubTitle>
            <div className="chk-grid">
              <CheckItem on={d.leap_out_continued_leadership}>Continued Leadership</CheckItem>
              <CheckItem on={d.leap_out_paid_volunteer}>Paid / Volunteer Roles</CheckItem>
              <CheckItem on={d.leap_out_workforce}>Workforce Integration</CheckItem>
              <CheckItem on={d.leap_out_advocacy}>Advocacy Roles</CheckItem>
            </div>
            <SubTitle>Risk &amp; Safeguards</SubTitle>
            <div className="chk-grid">
              <CheckItem on={d.leap_risk_burnout}>Burnout Monitoring</CheckItem>
              <CheckItem on={d.leap_risk_emotional_support}>Emotional Support</CheckItem>
              <CheckItem on={d.leap_risk_boundaries}>Boundaries Enforcement</CheckItem>
              <CheckItem on={d.leap_risk_crisis_protocol}>Crisis Protocol</CheckItem>
            </div>
          </div>

          {/* 12. DOCUMENTATION & QUALITY */}
          <div className="section">
            <SectionTitle>12. Documentation &amp; Quality (CARF Compliance)</SectionTitle>
            <div className="chk-grid">
              <CheckItem on={d.carf_person_centered}>Person-Centered Documentation</CheckItem>
              <CheckItem on={d.carf_cultural_sensitivity}>Cultural Sensitivity</CheckItem>
              <CheckItem on={d.carf_ethical_practice}>Ethical Practice</CheckItem>
              <CheckItem on={d.carf_measurable_outcomes}>Measurable Outcomes</CheckItem>
              <CheckItem on={d.carf_quality_improvement}>Continuous Quality Improvement</CheckItem>
              <CheckItem on={d.carf_client_family_feedback}>Client &amp; Family Feedback</CheckItem>
            </div>
          </div>

          {/* 13. PROGRAM DURATION */}
          <div className="section">
            <SectionTitle>13. Program Duration (Individualized)</SectionTitle>
            <SubTitle>Duration informed by</SubTitle>
            <div className="chk-grid">
              <CheckItem on={d.dur_strengths}>Strengths</CheckItem>
              <CheckItem on={d.dur_motivation}>Motivation</CheckItem>
              <CheckItem on={d.dur_social_support}>Social Support</CheckItem>
              <CheckItem on={d.dur_financial_capacity}>Financial Capacity</CheckItem>
              <CheckItem on={d.dur_occupational_readiness}>Occupational Readiness</CheckItem>
              <CheckItem on={d.dur_recovery_capital}>Recovery Capital</CheckItem>
            </div>
            <table>
              <tbody>
                <tr><td className="lbl">Estimated Duration</td><td>{val(d.estimated_duration)}</td><td className="lbl">Actual End Date</td><td>{fmt(d.actual_end_date)}</td></tr>
              </tbody>
            </table>
          </div>

          {/* FINAL SIGN-OFF */}
          <div className="section">
            <SectionTitle>Final Collaborative Sign-Off</SectionTitle>
            <table className="signoff">
              <thead>
                <tr><th style={{ width: "30%" }}>Role</th><th>Signature</th><th style={{ width: "22%" }}>Date</th></tr>
              </thead>
              <tbody>
                {[
                  ["Client", d.client_signature, d.client_sign_date],
                  ["Family Representative", d.family_rep_signature, d.family_rep_sign_date],
                  ["Consultant Psychiatrist", d.psychiatrist_signature, d.psychiatrist_sign_date],
                  ["PRP", d.prp_signature, d.prp_sign_date],
                  ["Case Manager", d.case_manager_signature, d.case_manager_sign_date],
                  ["LEAP Member (if applicable)", d.leap_member_signature, d.leap_member_sign_date],
                ].map(([role, sig, date]) => (
                  <tr key={role}>
                    <td>{role}</td>
                    <td>
                      {sigSrc(sig) ? (
                        <img src={sigSrc(sig)} alt={`${role} signature`} style={{ height: 40, maxWidth: 200, objectFit: "contain" }} />
                      ) : ""}
                    </td>
                    <td>{date ? fmt(date) : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="footer-badges">
            ✔ CARF-Compliant &middot; ✔ Recovery-Oriented &middot; ✔ Family &amp; Client Co-Ownership &middot; ✔ LEAP-Integrated &middot; ✔ Evidence-Based
          </div>
          <div className="confidential">CONFIDENTIAL — For Authorised Clinical Use Only</div>
        </div>
      </PrintWrapper>
    </>
  );
}
